import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Lookup from '@/api/lookup/api-lookup';
import { Delete, Get, PostBlob, Post, Put } from '@/api/api-middleware';
import { getCompanyID, getEmployeeID } from '@/helpers/Utils';
import { firstListItem, itemPerPage } from '@/constants/defaultValues';
import { apiPath } from '@/constants/defaultValues';
import { getLabel } from '@/helpers/Utils';

const initialState = {
  loading: false,
  AddWorkScheduleResponse: [],
  PaginatedWorkScheduleResponse: [],
  WorkScheduleList: [],
  notification: { show: false, code: '' },
  WorkScheduleItem: {},
  employeePermissionData: {},
  AddUpdateDeleteAssignment: {},
  AddUpdateDeleteRequest: {},
  AssignedScheduleList: [],
  permissionRequestSelected: {},
  error: '',
};

export const fetchPermissionByID = createAsyncThunk(
  'workSchedule/fetchPermissionByID',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}${'api/Operations/PermissionRequest/GetByID/'}${
          params?.id
        }`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const AddNewWorkSchedule = createAsyncThunk(
  'workSchedule/AddWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/Add'}`,
        data: { ...params, companyID: getCompanyID() },
      });
      thunkAPI.dispatch(
        fetchPaginatedWorkSchedule({
          pageNumber: 1,
          pageSize: itemPerPage,
          keyword: '',
        })
      );
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchPaginatedWorkSchedule = createAsyncThunk(
  'workSchedule/fetchPaginatedWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}${
          'api/Lookup/WorkSchedule/GetAllPaginated' + '/' + getCompanyID() + '/'
        }${params.pageNumber + '/'}${params.pageSize + '/'}${params.keyword}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getWorkScheduleById = createAsyncThunk(
  'workSchedule/getWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/WorkSchedule/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const UpdateWorkSchedule = createAsyncThunk(
  'workSchedule/UpdateWorkSchedule',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Lookup/WorkSchedule/Update'}`,
        data: { ...params, companyID: getCompanyID() },
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getAllSchedulesByCompanyId = createAsyncThunk(
  'workSchedule/getAllSchedulesByCompanyId',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/WorkSchedule/GetAll/${getCompanyID()}`,
      });
      const mappedData =
        response?.data?.data?.map((item) => ({
          id: item.id,
          nameEn: item.nameEn,
          nameAr: item.nameAr,
          label: getLabel(item.nameAr, item.nameEn),
          value: item.id,
        })) ?? [];
      return [firstListItem, ...mappedData];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getEmployeePermissionDataRequest = createAsyncThunk(
  'workSchedule/EmployeePermissionDataRequest',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}api/Operations/PermissionRequest/EmployeePermissionDataRequest`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteWorkScheduleById = createAsyncThunk(
  'workSchedule/deleteWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Lookup/WorkSchedule/Delete/${params}`,
      });
      thunkAPI.dispatch(
        fetchPaginatedWorkSchedule({
          pageNumber: 1,
          pageSize: itemPerPage,
          keyword: '',
        })
      );
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const AddPermissionRequest = createAsyncThunk(
  'permissionRequest/AddPermissionRequest',
  async (params, thunkAPI) => {
    try {
      console.log(params?.id);

      // Determine if this is an update or add request
      const url = params?.id
        ? `${apiPath}api/Operations/PermissionRequest/Update/${params.id.toString()}`
        : `${apiPath}api/Operations/PermissionRequest/Add`;

      const method = params?.id ? Put : Post;

      // Make the request
      const response = await method({
        url,
        data: params,
      });
      // Handle successful response
      if (response?.data?.errCode === '200') {
        return response?.data?.data;
      }

      // Handle failure case
      return thunkAPI.rejectWithValue({
        errCode:
          response?.data?.errCode == '600'
            ? response?.data?.data
            : response?.data?.errCode,
        message: response?.data || 'Request failed.',
      });
    } catch (error) {
      // General error handling
      return thunkAPI.rejectWithValue({
        message: error?.message || 'An unexpected error occurred',
      });
    }
  }
);

export const GetAssignedSchedules = createAsyncThunk(
  'workSchedule/GetAssignedSchedules',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/WorkScheduleAssignment/Get'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const permissionRequestSlice = createSlice({
  name: 'permissionRequest',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.AddWorkScheduleResponse = [];
      state.PaginatedWorkScheduleResponse = [];
      state.WorkScheduleItem = {};
      state.permissionRequestSelected = {};
      state.notification = { show: false, code: '' };
      state.loading = false,
      state.WorkScheduleList = [],
      state.employeePermissionData = {},
      state.AddUpdateDeleteAssignment = {},
      state.AddUpdateDeleteRequest = {},
      state.AssignedScheduleList = [],
      state.error = ''
    },
    clearNotification: (state) => {
      state.notification = { show: false, code: '' };
      state.loading = false;
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(AddNewWorkSchedule.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(AddNewWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(AddNewWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchPaginatedWorkSchedule.pending, (state) => {
        state.loading = true;
        state.WorkScheduleItem = {};
        state.AddWorkScheduleResponse = [];
        // state.notification = {show : false, code : ""};
      })
      .addCase(fetchPaginatedWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.PaginatedWorkScheduleResponse = action.payload;
        state.error = '';
      })
      .addCase(fetchPaginatedWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.PaginatedWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = action.payload;
        state.error = '';
      })
      .addCase(getWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = {};
        state.error = action.payload ?? 'An error occurred';
      });
    builder
      .addCase(getEmployeePermissionDataRequest.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getEmployeePermissionDataRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.employeePermissionData = action.payload;
        state.error = '';
      })
      .addCase(getEmployeePermissionDataRequest.rejected, (state, action) => {
        state.loading = false;
        state.employeePermissionData = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(UpdateWorkSchedule.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.AddWorkScheduleResponse = [];
      })
      .addCase(UpdateWorkSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(UpdateWorkSchedule.rejected, (state, action) => {
        state.loading = false;
        state.AddWorkScheduleResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getAllSchedulesByCompanyId.pending, (state) => {
        state.loading = true;
        state.WorkScheduleList = [];
        state.notification = { show: false, code: '' };
      })
      .addCase(getAllSchedulesByCompanyId.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkScheduleList = action.payload;
        state.error = '';
      })
      .addCase(getAllSchedulesByCompanyId.rejected, (state, action) => {
        state.loading = false;
        state.WorkScheduleList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(deleteWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deleteWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deleteWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(AddPermissionRequest.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(AddPermissionRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteRequest = { status: true };
        state.permissionRequestSelected = {};
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(AddPermissionRequest.rejected, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteRequest = {};
        let code = action.payload?.errCode;

        if (code === '-3') {
          code = 'lessDuration';
        } else if (code === '-4') {
          code = 'greatDuration';
        } else if (code === '-5') {
          code = 'exceedDailyLimit';
        } else if (code === '-6') {
          code = 'exceedAllowedAfterDays';
        } else if (code === '-7') {
          code = 'exceedAllowedBeforeDays';
        } else if (code === '-8') {
          code = 'exceedMonthlyLimit';
        } else if (code === '-10') {
          code = 'AlreadyHaveOnePemissionInSameRange';
        } else if (code === '-11') {
          code = '-11';
        } else {
          code = 'generalError';
        }

        state.notification = {
          show: true,
          code: code ?? '500',
        };
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(GetAssignedSchedules.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(GetAssignedSchedules.fulfilled, (state, action) => {
        state.loading = false;
        state.AssignedScheduleList = action.payload;
        state.error = '';
      })
      .addCase(GetAssignedSchedules.rejected, (state, action) => {
        state.loading = false;
        state.AssignedScheduleList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchPermissionByID.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchPermissionByID.fulfilled, (state, action) => {
        state.loading = false;
        state.permissionRequestSelected = action.payload;
        state.error = '';
      })
      .addCase(fetchPermissionByID.rejected, (state, action) => {
        state.loading = false;
        state.permissionRequestSelected = {};
        state.error = action.payload ?? 'An error occurred';
      });
  },
});
export const { clearCurrentItem, clearNotification } =
  permissionRequestSlice.actions;
export default permissionRequestSlice.reducer;
